import React, { useState, useCallback } from 'react';
import { Attachment } from '../../types/chat';
import './ImageViewer.css';

interface ImageViewerProps {
  attachment: Attachment;
  className?: string;
  onClick?: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ attachment, className, onClick }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // 画像URLを取得（S3 URLを優先）
  const imageUrl = attachment.s3_url || attachment.url;

  const handleImageLoad = useCallback(() => {
    setLoading(false);
    setError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setLoading(false);
    setError(true);
    console.error('Failed to load image:', imageUrl);
  }, [imageUrl]);

  const handleImageClick = useCallback(() => {
    if (!error) {
      setShowModal(true);
      onClick?.();
    }
  }, [error, onClick]);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleModalClick = useCallback((e: React.MouseEvent) => {
    // モーダル背景をクリックした場合のみ閉じる
    if (e.target === e.currentTarget) {
      handleModalClose();
    }
  }, [handleModalClose]);

  return (
    <>
      <div className={`image-viewer ${className || ''}`}>
        {loading && (
          <div className="image-viewer__loading">
            <div className="spinner"></div>
            <span>画像を読み込み中...</span>
          </div>
        )}
        
        {error && (
          <div className="image-viewer__error">
            <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>画像を読み込めませんでした</span>
            <span className="filename">{attachment.filename}</span>
          </div>
        )}
        
        {!error && (
          <div 
            className="image-viewer__container"
            onClick={handleImageClick}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={imageUrl}
              alt={attachment.filename}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ 
                display: loading ? 'none' : 'block',
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px'
              }}
            />
            {!loading && (
              <div className="image-viewer__overlay">
                <svg className="expand-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 画像拡大モーダル */}
      {showModal && !error && (
        <div className="image-modal" onClick={handleModalClick}>
          <div className="image-modal__content">
            <button className="image-modal__close" onClick={handleModalClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={imageUrl}
              alt={attachment.filename}
              className="image-modal__image"
            />
            <div className="image-modal__info">
              <span className="filename">{attachment.filename}</span>
              {attachment.width && attachment.height && (
                <span className="dimensions">{attachment.width} × {attachment.height}</span>
              )}
              <span className="size">{formatFileSize(attachment.size)}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ファイルサイズをフォーマット
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

export default ImageViewer;